import { AuthLayout } from "@/components/auth/AuthLayout";
import { authClerkAppearance } from "@/components/auth/clerkAppearance";
import { Button } from "@/components/ui/button";
import { getSignInStepCopy } from "@/features/auth/signInStepCopy";
import {
  appendInvitationSearch,
  readInvitationParams,
} from "@/features/onboarding/invitationParams";
import { hasOnboardingInProgress } from "@/features/onboarding/useOnboardingController";

import { SignIn, useAuth, useSignIn } from "@clerk/react";
import { Loader2 } from "lucide-react";
import { Link, Redirect } from "wouter";

function readRedirectUrl(): string | null {
  if (typeof window === "undefined") return null;
  const param = new URLSearchParams(window.location.search).get("redirect_url");
  if (!param) return null;
  // Only allow same-origin relative paths to avoid open-redirect risk.
  if (!param.startsWith("/") || param.startsWith("//")) return null;
  return param;
}

export default function Login() {
  const { isLoaded, isSignedIn, userId } = useAuth();
  const { signIn } = useSignIn();
  const redirectUrl = readRedirectUrl();
  const invitationParams = readInvitationParams();
  const stepCopy = getSignInStepCopy(signIn);

  // Mounting Clerk's widget before the SDK is ready leaves the form blank, and
  // it stays blank until a reload (when Clerk is already initialised).
  if (!isLoaded) {
    return (
      <AuthLayout mode="sign-in">
        <div className="flex flex-col items-center gap-3 py-10" role="status">
          <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden="true" />
          <p className="text-sm text-muted-foreground">Loading sign-in…</p>
        </div>
      </AuthLayout>
    );
  }

  if (isSignedIn) {
    if (invitationParams.hasInvitationContext) {
      return <Redirect to={appendInvitationSearch("/accept-invitation")} />;
    }
    if (hasOnboardingInProgress(userId ?? undefined)) {
      return <Redirect to="/onboarding" />;
    }
    return <Redirect to={redirectUrl ?? "/"} />;
  }

  const fallbackRedirect = invitationParams.hasInvitationContext
    ? appendInvitationSearch("/accept-invitation")
    : hasOnboardingInProgress(userId ?? undefined)
      ? "/onboarding"
      : (redirectUrl ?? "/");

  return (
    <AuthLayout mode="sign-in">
      <div className="flex flex-col gap-6">
        <div className="flex flex-col space-y-2 text-center">
          <h1 className="text-2xl font-semibold tracking-tight">{stepCopy.title}</h1>
          <p className="text-sm text-muted-foreground">{stepCopy.description}</p>
        </div>
        <div className="auth-clerk w-full px-2">
          <SignIn
            routing="hash"
            fallbackRedirectUrl={fallbackRedirect}
            appearance={authClerkAppearance}
          />
        </div>
        
        {/* Mobile CTA for new users */}
        <div className="block md:hidden">
          <div className="text-center text-sm text-muted-foreground mb-3">
            New to A Safer Risk?
          </div>
          <Button asChild variant="outline" size="lg" className="w-full">
            <Link href="/free-trial">Start your free trial</Link>
          </Button>
        </div>
        
        <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 hover:[&_a]:text-primary">
          By logging in, you agree to our <a href="/terms-of-service">Terms of Service</a>{" "}
          and <a href="/privacy-policy">Privacy Policy</a>.
        </div>
      </div>
    </AuthLayout>
  );
}
